import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  TextField,
  Chip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { WorkspacesService } from '../../services/WorkspacesService';
import { NotificationSnackbar } from '../../components/notification-snackbar/notification-snackbar';
import { useNotification } from '../../hooks/useNotification';
import { TasksDialog } from './TasksDialog';
import { PageResponse, Provision, Environment } from './types';

const useStyles = makeStyles(theme => ({
  filtersRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  formControl: {
    minWidth: 200,
  },
  autocomplete: {
    minWidth: 250,
  },
  tableWrapper: {
    backgroundColor: theme.palette.background.paper,
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  statusChip: {
    color: '#fff',
    fontWeight: 'bold',
  },
}));

const STATUS_OPTIONS = [
  { label: 'Todos', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Running', value: 'RUNNING' },
  { label: 'Succeeded', value: 'SUCCEEDED' },
  { label: 'Failed', value: 'FAILED' },
] as const;

const STATUS_COLORS = {
  PENDING: '#9E9E9E',
  RUNNING: '#3F51B5',
  SUCCEEDED: '#4CAF50',
  FAILED: '#E53935',
} as const;

const getStatusColor = (status: string): string => {
  const normalizedStatus = status?.toUpperCase() as keyof typeof STATUS_COLORS;
  return STATUS_COLORS[normalizedStatus] || STATUS_COLORS.PENDING;
};

const INITIAL_PAGE_DATA: PageResponse<Provision> = {
  items: [],
  hasNext: false,
};

const AUTOCOMPLETE_DEBOUNCE_DELAY = 300;
const INITIAL_PAGE = 1;
const PAGE_SIZE = 25;
const TABLE_COLUMNS_COUNT = 7;

interface WorkspaceOption {
  id: string;
  name: string;
}

interface AccountOption {
  id: string;
  name: string;
}

export const RequestsTab = ({ environment }: { environment: Environment }) => {
  const classes = useStyles();
  const { notification, showError, hideNotification } =
    useNotification();

  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const catalogApi = useApi(catalogApiRef);

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, microsoftAuthApi, discoveryApi, catalogApi),
    [fetchApi, microsoftAuthApi, discoveryApi, catalogApi],
  );

  const [statusFilter, setStatusFilter] = useState('');
  const [systemFilter, setSystemFilter] = useState('');
  const [workspaceFilter, setWorkspaceFilter] = useState<WorkspaceOption | null>(null);
  const [workspaceInputValue, setWorkspaceInputValue] = useState('');
  const [workspaceOptions, setWorkspaceOptions] = useState<WorkspaceOption[]>([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [accountFilter, setAccountFilter] = useState<AccountOption | null>(null);
  const [accountInputValue, setAccountInputValue] = useState('');
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [accountLoading, setAccountLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [pageData, setPageData] = useState<PageResponse<Provision>>(INITIAL_PAGE_DATA);
  const [items, setItems] = useState<Provision[]>([]);
  const [availableSystems, setAvailableSystems] = useState<string[]>([]);
  const [selectedProvisionId, setSelectedProvisionId] = useState<string | null>(null);
  const [tasksDialogOpen, setTasksDialogOpen] = useState(false);

  const hasActiveFilters = useMemo(
    () => Boolean(statusFilter || systemFilter || workspaceFilter || accountFilter),
    [statusFilter, systemFilter, workspaceFilter, accountFilter],
  );

  const extractSystemsOptions = useCallback((data: Provision[]) => {
    const systems = Array.from(
      new Set(data.flatMap(item => item.systemIds || []).filter(Boolean)),
    ).sort();
    setAvailableSystems(systems);
  }, []);

  const fetchAccounts = useCallback(
    async (searchTerm: string) => {
      setAccountLoading(true);

      try {
        const accountsResponse = await workspacesService.searchAccounts(environment, {
          search: searchTerm || undefined,
        });

        const mappedAccounts: AccountOption[] = (accountsResponse.items || []).map(account => ({
          id: account.id,
          name: account.totvsCode,
        }));

        setAccountOptions(mappedAccounts);
      } catch (error) {
        console.error('Error fetching accounts:', error);
        setAccountOptions([]);
      } finally {
        setAccountLoading(false);
      }
    },
    [workspacesService, environment],
  );

  const fetchWorkspaces = useCallback(
    async (accountId: string, searchTerm?: string) => {
      setWorkspaceLoading(true);

      try {
        const workspacesResponse = await workspacesService.searchWorkspacesByAccount(
          accountId,
          environment,
          searchTerm ? { name: searchTerm } : undefined,
        );

        const mappedWorkspaces = (workspacesResponse.items || []).map(ws => ({
          id: ws.id,
          name: ws.name,
        }));

        setWorkspaceOptions(mappedWorkspaces);
      } catch (error) {
        console.error('Error fetching workspaces:', error);
        setWorkspaceOptions([]);
      } finally {
        setWorkspaceLoading(false);
      }
    },
    [workspacesService, environment],
  );

  const fetchProvisions = useCallback(
    async (reset = false) => {
      if (loading || loadingMore) return;

      if (reset) {
        setLoading(true);
        setItems([]);
      } else {
        setLoadingMore(true);
      }

      try {
        const targetPage = reset ? INITIAL_PAGE : page;

        const response = await workspacesService.listProvisions(environment, {
          workspaceId: workspaceFilter?.id || undefined,
          status: statusFilter || undefined,
          system: systemFilter || undefined,
          page: targetPage,
          size: PAGE_SIZE,
        });

        if (!response?.items || !Array.isArray(response.items)) {
          throw new Error('Invalid API response format');
        }

        setPageData(response);

        if (reset) {
          setItems(response.items);
          setPage(INITIAL_PAGE);
          extractSystemsOptions(response.items);
        } else {
          setItems(prev => [...prev, ...response.items]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unknown error fetching provisions';
        console.error('Error fetching provisions:', error);
        showError(errorMessage);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [
      loading,
      loadingMore,
      page,
      statusFilter,
      systemFilter,
      workspaceFilter,
      environment,
      workspacesService,
      extractSystemsOptions,
      showError,
    ],
  );

  const handleOpenTasks = useCallback((provisionId: string) => {
    setSelectedProvisionId(provisionId);
    setTasksDialogOpen(true);
  }, []);

  const handleCloseTasksDialog = useCallback(() => {
    setTasksDialogOpen(false);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!pageData.hasNext || loadingMore) return;
    setPage(prev => prev + 1);
  }, [pageData, loadingMore]);

  const handleStatusChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setStatusFilter(event.target.value as string);
    },
    [],
  );

  const handleSystemChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setSystemFilter(event.target.value as string);
    },
    [],
  );

  const handleWorkspaceChange = useCallback(
    (_event: React.ChangeEvent<{}>, newValue: WorkspaceOption | null) => {
      setWorkspaceFilter(newValue);
    },
    [],
  );

  const handleAccountChange = useCallback(
    (_event: React.ChangeEvent<{}>, newValue: AccountOption | null) => {
      setAccountFilter(newValue);
      setWorkspaceFilter(null);
      setWorkspaceInputValue('');
      setWorkspaceOptions([]);
      if (newValue) {
        fetchWorkspaces(newValue.id);
      }
    },
    [fetchWorkspaces],
  );

  const handleClearFilters = useCallback(() => {
    setStatusFilter('');
    setSystemFilter('');
    setWorkspaceFilter(null);
    setWorkspaceInputValue('');
    setWorkspaceOptions([]);
    setAccountFilter(null);
    setAccountInputValue('');
    setAccountOptions([]);
  }, []);

  useEffect(() => {
    handleClearFilters();
  }, [environment, handleClearFilters]);

  useEffect(() => {
    fetchProvisions(true);
  }, [statusFilter, systemFilter, workspaceFilter, accountFilter]);

  useEffect(() => {
    if (page === INITIAL_PAGE) return;
    
    fetchProvisions(false);
  }, [page]);

  useEffect(() => {
    if (!accountInputValue) return;

    const timeoutId = setTimeout(() => {
      fetchAccounts(accountInputValue);
    }, AUTOCOMPLETE_DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [accountInputValue, fetchAccounts]);

  useEffect(() => {
    if (!accountFilter || !workspaceInputValue) return;

    const timeoutId = setTimeout(() => {
      fetchWorkspaces(accountFilter.id, workspaceInputValue);
    }, AUTOCOMPLETE_DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [workspaceInputValue, accountFilter, fetchWorkspaces]);

  const renderTableBody = () => {
    if (items.length > 0) {
      return items.map((item, index) => (
        <TableRow key={item.id ?? index}>
          <TableCell>{item.id}</TableCell>
          <TableCell>{item.origin}</TableCell>
          <TableCell>
            <Chip
              label={item.status || 'Unknown'}
              className={classes.statusChip}
              style={{
                backgroundColor: getStatusColor(item.status || ''),
              }}
              size="small"
            />
          </TableCell>
          <TableCell>{item.createdAt}</TableCell>
          <TableCell align="center">
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleOpenTasks(item.id)}
            >
              Open tasks
            </Button>
          </TableCell>
        </TableRow>
      ));
    }

    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={TABLE_COLUMNS_COUNT} className={classes.emptyState}>
            <CircularProgress />
            <Typography>Loading provisions...</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow>
        <TableCell colSpan={TABLE_COLUMNS_COUNT} className={classes.emptyState}>
          <Typography variant="h6">No provision found</Typography>
          <Typography variant="body2" color="textSecondary">
            Adjust filters to see results.
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <Box className={classes.filtersRow}>
        <Autocomplete
          className={classes.autocomplete}
          options={accountOptions}
          getOptionLabel={option => option.name}
          value={accountFilter}
          onChange={handleAccountChange}
          inputValue={accountInputValue}
          onInputChange={(_event, newInputValue) => {
            setAccountInputValue(newInputValue);
          }}
          loading={accountLoading}
          size="small"
          renderInput={params => (
            <TextField
              {...params}
              label="Account (TotvsCode)"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {accountLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="No account found"
        />

        <Autocomplete
          className={classes.autocomplete}
          options={workspaceOptions}
          getOptionLabel={option => option.name}
          value={workspaceFilter}
          onChange={handleWorkspaceChange}
          inputValue={workspaceInputValue}
          onInputChange={(_event, newInputValue) => {
            setWorkspaceInputValue(newInputValue);
          }}
          loading={workspaceLoading}
          disabled={!accountFilter}
          size="small"
          renderInput={params => (
            <TextField
              {...params}
              label="Workspace"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {workspaceLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="No workspace found"
        />

        <FormControl className={classes.formControl} variant="outlined" size="small">
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            onChange={handleStatusChange}
            label="Status"
          >
            {STATUS_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.formControl} variant="outlined" size="small">
          <InputLabel id="system-filter-label">System</InputLabel>
          <Select
            labelId="system-filter-label"
            value={systemFilter}
            onChange={handleSystemChange}
            label="System"
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {availableSystems.map(system => (
              <MenuItem key={system} value={system}>
                {system}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <Button
            variant="outlined"
            color="default"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
          >
            Clear filters
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="textSecondary">
        Displaying {items.length} provisions{' '}
        {hasActiveFilters && '(active filters)'}
      </Typography>

      <Box className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Origin</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </Box>

      <Box className={classes.loadMoreContainer}>
        {loadingMore && <CircularProgress />}
        {!loadingMore && pageData.hasNext && items.length > 0 && (
          <Button variant="outlined" onClick={handleLoadMore}>
            Load more items
          </Button>
        )}

        <NotificationSnackbar
          notification={notification}
          onClose={hideNotification}
        />

        <TasksDialog
          open={tasksDialogOpen}
          onClose={handleCloseTasksDialog}
          provisionId={selectedProvisionId}
          environment={environment}
          workspacesService={workspacesService}
        />
      </Box>
    </Box>
  );
};
